/**
 * Beat-mark aware svara formatting. Direct port of
 * jnana-native/src/util/svaras.ts → formatSvaras.
 *
 * The bhajan's talam carries a `beatPattern` like
 *   `"| , , | , , | , , | , , |"`
 * where:
 *   `|`  = single bar (BeatMark.Single)
 *   `‖`  = double bar (BeatMark.Double)
 *   `,`  = beat slot — receives one group of svaras
 *
 * We split the raw svara line evenly across the comma slots, then re-emit
 * the line with the bars in their right places. If the svara count isn't
 * cleanly divisible by the comma count, we return the input unchanged
 * (so we don't garble notation we can't reliably partition).
 */

export const BeatMark = {
  Single: '|',
  Double: '‖',
  Beat: ',',
} as const;

export function formatSvaras(svaras: string, beatPattern: string | null | undefined): string {
  if (!svaras || !beatPattern) return svaras;
  try {
    const svaraTokens = svaras.split(/\s+/).filter((t) => t.length > 0);
    const beatTokens = beatPattern.split(/\s+/).filter((t) => t.length > 0);
    const commaCount = beatTokens.filter((t) => t === BeatMark.Beat).length;
    if (commaCount === 0) return svaras;
    if (svaraTokens.length % commaCount !== 0) return svaras;

    const svarasPerGroup = svaraTokens.length / commaCount;
    const groups: string[][] = Array.from({ length: commaCount }, (_, i) =>
      svaraTokens.slice(i * svarasPerGroup, (i + 1) * svarasPerGroup)
    );

    let g = 0;
    return beatTokens
      .map((tok) => {
        if (tok === BeatMark.Beat) {
          const grp = groups[g] ?? [];
          g++;
          return grp.join(' ');
        }
        return tok;
      })
      .join(' ');
  } catch {
    return svaras;
  }
}
